import { getSession } from '../neo4jDriver';
import { neo4jService } from './neo4jService';

// Customer Authentication
export const customerLogin = async (email, password) => {
  if (!email || !password) {
    throw new Error('Email and password are required');
  }

  try {
    const session = getSession();
    const result = await session.run(
      'MATCH (c:Customer {email: $email, password: $password, role: "customer"}) RETURN c',
      { email, password }
    );
    session.close();

    if (result.records.length === 0) {
      throw new Error('Invalid credentials');
    }

    const customer = result.records[0].get('c').properties;
    return {
      ...customer,
      role: 'customer'
    };
  } catch (error) {
    throw new Error('Login failed: ' + error.message);
  }
};

export const customerSignup = async (userData) => {
  if (!userData.email || !userData.password) {
    throw new Error('Email and password are required');
  }

  const result = await neo4jService.registerCustomer(userData);
  if (!result.success) {
    throw new Error(result.message);
  }
  
  // After successful registration, log in the user
  const user = await customerLogin(userData.email, userData.password);
  return user;
};

// Admin Authentication
export const adminLogin = async (email, password) => {
  try {
    const session = getSession();
    // First find the user by email only
    const result = await session.run(
      'MATCH (u:User {email: $email, role: "admin"}) RETURN u',
      { email }
    );
    session.close();

    if (result.records.length === 0) {
      throw new Error('Invalid admin credentials');
    }

    const user = result.records[0].get('u').properties;
    
    // Compare the passwords
    if (user.password !== password) {
      throw new Error('Invalid admin credentials');
    }

    return {
      ...user,
      role: 'admin'
    };
  } catch (error) {
    throw new Error('Admin login failed: ' + error.message);
  }
};

// Create Admin Account
export const createAdminAccount = async (adminData) => {
  const { email, password, name } = adminData;
  
  try {
    const session = getSession();
    
    // Check if user already exists
    const existingUser = await session.run(
      'MATCH (u:User {email: $email}) RETURN u',
      { email }
    );

    if (existingUser.records.length > 0) {
      session.close();
      throw new Error('User already exists');
    }

    // Create admin user
    const result = await session.run(
      `CREATE (u:User {
        id: randomUUID(),
        email: $email,
        password: $password,
        name: $name,
        role: "admin",
        createdAt: datetime()
      }) RETURN u`,
      { email, password, name }
    );
    session.close();

    const user = result.records[0].get('u').properties;
    return {
      ...user,
      role: 'admin'
    };
  } catch (error) {
    throw new Error('Failed to create admin account: ' + error.message);
  }
};

// Get All Admins
export const getAllAdmins = async () => {
  try {
    const session = getSession();
    const result = await session.run(
      'MATCH (u:User {role: "admin"}) RETURN u'
    );
    session.close();

    return result.records.map(record => {
      const user = record.get('u').properties;
      return {
        ...user,
        role: 'admin'
      };
    });
  } catch (error) {
    throw new Error('Failed to fetch admin accounts: ' + error.message);
  }
};

export const updateAdminAccount = async (adminId, adminData) => {
  const { email, password, name } = adminData;

  try {
    const session = getSession();

    const existingUser = await session.run(
      'MATCH (u:User {email: $email}) WHERE u.id <> $adminId RETURN u LIMIT 1',
      { email, adminId }
    );

    if (existingUser.records.length > 0) {
      session.close();
      throw new Error('User already exists');
    }

    const query = password
      ? `MATCH (u:User {id: $adminId, role: "admin"})
         SET u.email = $email,
             u.password = $password,
             u.name = $name,
             u.updatedAt = datetime()
         RETURN u`
      : `MATCH (u:User {id: $adminId, role: "admin"})
         SET u.email = $email,
             u.name = $name,
             u.updatedAt = datetime()
         RETURN u`;

    const result = await session.run(query, { adminId, email, password, name });
    session.close();

    if (result.records.length === 0) {
      throw new Error('Admin account not found');
    }

    const user = result.records[0].get('u').properties;
    return {
      ...user,
      role: 'admin'
    };
  } catch (error) {
    throw new Error('Failed to update admin account: ' + error.message);
  }
};

export const deleteAdminAccount = async (adminId) => {
  try {
    const session = getSession();

    const countResult = await session.run(
      'MATCH (u:User {role: "admin"}) RETURN count(u) as adminCount'
    );
    const adminCount = countResult.records[0].get('adminCount');
    const totalAdmins = typeof adminCount.toNumber === 'function'
      ? adminCount.toNumber()
      : Number(adminCount);

    if (totalAdmins <= 1) {
      session.close();
      throw new Error('At least one admin user must remain');
    }

    const result = await session.run(
      `MATCH (u:User {id: $adminId, role: "admin"})
       WITH collect(u) as admins, count(u) as deletedCount
       FOREACH (admin IN admins | DELETE admin)
       RETURN deletedCount`,
      { adminId }
    );
    session.close();

    const deletedCount = result.records[0].get('deletedCount');
    const deleted = typeof deletedCount.toNumber === 'function'
      ? deletedCount.toNumber()
      : Number(deletedCount);

    if (deleted === 0) {
      throw new Error('Admin account not found');
    }

    return true;
  } catch (error) {
    throw new Error('Failed to delete admin account: ' + error.message);
  }
};

// Check if any admin account exists
export const checkAdminExists = async () => {
  try {
    const session = getSession();
    const result = await session.run(
      'MATCH (u:User {role: "admin"}) RETURN u LIMIT 1'
    );
    session.close();
    return result.records.length > 0;
  } catch (error) {
    throw new Error('Failed to check admin accounts: ' + error.message);
  }
};

// Initialize First Admin Account
export const initializeFirstAdmin = async (email, password, name) => {
  try {
    const session = getSession();
    
    // Check if any admin exists
    const existingAdmin = await session.run(
      'MATCH (u:User {role: "admin"}) RETURN u LIMIT 1'
    );

    if (existingAdmin.records.length > 0) {
      session.close();
      throw new Error('Admin account already exists');
    }

    // Create first admin user
    const result = await session.run(
      `CREATE (u:User {
        id: randomUUID(),
        email: $email,
        password: $password,
        name: $name,
        role: "admin",
        createdAt: datetime()
      }) RETURN u`,
      { email, password, name }
    );
    session.close();

    const user = result.records[0].get('u').properties;
    return {
      ...user,
      role: 'admin'
    };
  } catch (error) {
    throw new Error('Failed to initialize admin account: ' + error.message);
  }
};

export const signOut = async () => {
  // For Neo4j, we just need to clear the local session
  localStorage.removeItem('user');
};
